import {
  IntegrationsDemoPage,
  IntegrationsRouteModal,
  SETTING_PAGE_ID_URL_PARAM_NAME,
} from '@portals/framework';
import { ReactComponent as Box } from '@portals/icons/linear/box.svg';
import { ReactComponent as CloudConnection } from '@portals/icons/linear/cloud-connection.svg';
import { ReactComponent as Code } from '@portals/icons/linear/code.svg';
import { ReactComponent as ColorSwatch } from '@portals/icons/linear/color-swatch.svg';
import { ReactComponent as Danger } from '@portals/icons/linear/danger.svg';
import { ReactComponent as EmptyWallet } from '@portals/icons/linear/empty-wallet.svg';
import { ReactComponent as FolderCloud } from '@portals/icons/linear/folder-cloud.svg';
import { ReactComponent as Health } from '@portals/icons/linear/health.svg';
import { ReactComponent as Hierarchy3 } from '@portals/icons/linear/hierarchy-3.svg';
import { ReactComponent as MonitorMobbile } from '@portals/icons/linear/monitor-mobbile.svg';
import { ReactComponent as Setting2 } from '@portals/icons/linear/setting-2.svg';
import { ReactComponent as Shop } from '@portals/icons/linear/shop.svg';
import { ReactComponent as Ticket } from '@portals/icons/linear/ticket.svg';
import { ReactComponent as UserSquare } from '@portals/icons/linear/user-square.svg';
import {
  CategoryRouteItem,
  PartnerFeatureFlagsType,
  PartnerPortalCapabilities,
  PartnerPricingPlanFeatures,
  RoutesMap,
} from '@portals/types';

import { ReactComponent as CustomersIcon } from './assets/img/icons/sidebar/customers.svg';
import { ReactComponent as DashboardIcon } from './assets/img/icons/sidebar/dashboard.svg';
import { ReactComponent as HomeIcon } from './assets/img/icons/sidebar/home.svg';
import { ReactComponent as ModelsIcon } from './assets/img/icons/sidebar/models.svg';
import { ReactComponent as WhitelabelIcon } from './assets/img/icons/sidebar/whitelabel.svg';
import OnboardingWizard from './components/OnboardingWizard';
import { PartnerToPartnerStatus } from './components/PartnerToPartnerStatus';
import { TicketsCounter, TicketsIndicator } from './components/TicketsCounter';
import { Branding } from './pages/branding/Branding';
import { CreditProviders } from './pages/credit-providers';
import { Customers } from './pages/customers';
import Dashboard from './pages/dashboard';
import { DevCenter } from './pages/dev-center';
import { Devices } from './pages/devices/Devices';
import { Files } from './pages/files';
import { Home } from './pages/home';
import Incidents from './pages/incidents';
import Models from './pages/models';
import { Organizations } from './pages/organizations';
import { Partners } from './pages/partners/Partners';
import { ProductsCatalogPage } from './pages/products-catalog/ProductsCatalogPage';
import Rules from './pages/rules';
import { Settings } from './pages/settings/Settings';
import { CreateTaxGroup } from './pages/settings/store-settings/tax-groups/create-tax-group';
import { StoreListingsPage } from './pages/store-listings/StoreListingsPage';
import { FinanceOverview } from './pages/store-management/pages/finance-overview/FinanceOverview';
import { Invoices } from './pages/store-management/pages/invoices/Invoices';
import { Orders } from './pages/store-management/pages/orders/Orders';
import { PurchasedProducts } from './pages/store-management/pages/purchased-products/PurchasedProducts';
import { Shipping } from './pages/store-management/pages/shipping/Shipping';
import { Subscriptions } from './pages/store-management/pages/subscriptions/Subscriptions';
import { StoreManagement } from './pages/store-management/StoreManagement';
import { SupportOverview } from './pages/support-overview';
import { Tickets } from './pages/tickets';
import { CreditProviders as CreditProvidersRouteModal } from './route-modals/CreditProviders';
import { Customer } from './route-modals/Customer';
import { Order } from './route-modals/Order/Order';

const dashboard: CategoryRouteItem<
  PartnerPortalCapabilities,
  PartnerFeatureFlagsType,
  PartnerPricingPlanFeatures
>[] = [
  {
    id: 'dashboard',
    headerId: '',
    canAccessRoute: ({ pricingPlanFeatures }) =>
      pricingPlanFeatures?.device_models_limit > 0,
    childRoutes: [
      {
        path: ['/', '/home'],
        id: 'sidebar.home',
        icon: HomeIcon,
        component: Home,
        canAccessRoute: ({ featureFlags, isAdmin }) =>
          featureFlags?.home === true && isAdmin,
      },
      {
        path: ({ featureFlags, isAdmin }) =>
          featureFlags?.home === true && isAdmin
            ? ['/dashboard']
            : ['/', '/dashboard'],
        id: 'sidebar.dashboard',
        icon: DashboardIcon,
        component: Dashboard,
      },
    ],
  },
  {
    id: 'configuration',
    headerId: 'sidebar.configuration',
    childRoutes: [
      {
        path: ['/models', '/models/:id', '/models/:id/:tab'],
        id: 'sidebar.models',
        icon: ModelsIcon,
        component: Models,
        canAccessRoute: ({ canView, hasSupportSeat, pricingPlanFeatures }) => {
          if (pricingPlanFeatures?.device_models_limit === 0) return false;
          return hasSupportSeat || canView(['models']);
        },
      },
      {
        path: ['/devices', '/devices/claimed', '/devices/unclaimed'],
        id: 'sidebar.devices',
        icon: MonitorMobbile,
        component: Devices,
        canAccessRoute: ({ canView, hasSupportSeat, pricingPlanFeatures }) => {
          if (pricingPlanFeatures?.device_models_limit === 0) return false;

          return hasSupportSeat || canView(['models', 'fulfillment']);
        },
      },
      {
        path: ['/files', '/files/:id'],
        id: 'sidebar.files',
        icon: FolderCloud,
        component: Files,
        canAccessRoute: ({ canView, hasSupportSeat, pricingPlanFeatures }) => {
          if (pricingPlanFeatures?.device_models_limit === 0) return false;

          return hasSupportSeat || canView('models');
        },
      },
      {
        path: ['/rules/:id'],
        id: 'sidebar.incidentRules',
        component: Rules,
        isVisibleInSidebar: () => false,
        canAccessRoute: ({ pricingPlanFeatures, canView, hasSupportSeat }) => {
          if (pricingPlanFeatures?.device_models_limit === 0) return false;

          return hasSupportSeat || canView('models');
        },
      },
      {
        path: ['/catalog', '/catalog/:tab'],
        id: 'sidebar.catalog',
        icon: ColorSwatch,
        component: ProductsCatalogPage,
        canAccessRoute: ({ hasSupportSeat, canView, classification }) =>
          (hasSupportSeat || canView('store_management')) &&
          classification !== 'channel_partner',
        isVisibleInSidebar: ({ classification }) =>
          classification !== 'channel_partner',
      },
      {
        path: ['/branding', '/branding/:type'],
        id: 'sidebar.branding',
        icon: WhitelabelIcon,
        component: Branding,
        canAccessRoute: ({ isAdmin, classification }) =>
          isAdmin && classification !== 'channel_partner',
      },
    ],
  },
  {
    id: 'yourStore',
    headerId: 'sidebar.yourStore',
    canAccessRoute: ({ pricingPlanFeatures }) =>
      pricingPlanFeatures?.store === 1,
    childRoutes: [
      {
        path: ['/products', '/products/:tab'],
        id: 'sidebar.products',
        icon: Box,
        component: StoreListingsPage,
        canAccessRoute: ({ hasSupportSeat, canView }) =>
          hasSupportSeat || canView('store_management'),
      },
      {
        path: ['/store-management', '/store-management/*'],
        id: 'sidebar.storeManagement',
        icon: Shop,
        canAccessRoute: ({ canView }) =>
          canView(['store_management', 'finance', 'fulfillment']),
        component: StoreManagement,
        children: [
          {
            path: [
              '/store-management/subscriptions',
              '/store-management/subscriptions/:tab',
            ],
            id: 'subscriptions',
            component: Subscriptions,
          },
          {
            path: [
              '/store-management/purchased-products',
              '/store-management/purchased-products/:tab',
            ],
            id: 'purchasedProducts',
            component: PurchasedProducts,
          },
          {
            path: ['/store-management/orders', '/store-management/orders/:tab'],
            id: 'orders',
            component: Orders,
          },
          {
            path: [
              '/store-management/shipping',
              '/store-management/shipping/:tab',
            ],
            id: 'shipping',
            component: Shipping,
          },
          {
            path: [
              '/store-management/invoices',
              '/store-management/invoices/:tab',
            ],
            id: 'invoices',
            component: Invoices,
          },
          {
            path: [
              '/store-management/finance-overview',
              '/store-management/finance-overview/:tab',
            ],
            id: 'financeOverview',
            component: FinanceOverview,
          },
        ],
      },
    ],
  },
  {
    id: 'supportAndMonitoring',
    headerId: 'sidebar.supportAndMonitoring',
    canAccessRoute: ({ hasSupportSeat, isAdmin, portalCapabilities }) =>
      isAdmin ||
      (hasSupportSeat && Boolean(portalCapabilities?.support_center)),
    childRoutes: [
      {
        path: ({ pricingPlanFeatures }) => {
          if (pricingPlanFeatures?.device_models_limit === 0) {
            return ['/', '/support-overview'];
          }
          return ['/support-overview'];
        },
        id: 'sidebar.supportOverview',
        icon: Health,
        component: SupportOverview,
      },
      {
        path: ['/organizations'],
        id: 'sidebar.organizations',
        icon: UserSquare,
        component: Organizations,
        canAccessRoute: ({ hasSupportSeat, isAdmin }) =>
          hasSupportSeat || isAdmin,
      },
      {
        path: ['/tickets', '/tickets/active', '/tickets/all', '/tickets/:id'],
        id: 'sidebar.tickets',
        icon: Ticket,
        component: Tickets,
        badgeComponent: TicketsCounter,
        indicatorComponent: TicketsIndicator,
        canAccessRoute: ({ pricingPlanFeatures }) =>
          pricingPlanFeatures?.device_models_limit > 0,
      },
      {
        path: ['/incidents', '/incidents/:id'],
        id: 'sidebar.incidents',
        icon: Danger,
        component: Incidents,
      },
    ],
  },
  {
    id: 'yourConnections',
    headerId: 'sidebar.yourConnections',
    canAccessRoute: ({ canView, hasSupportSeat, classification }) =>
      hasSupportSeat || canView(['customers', 'fulfillment', 'finance']),
    childRoutes: [
      {
        path: ['/customers'],
        id: 'sidebar.customers',
        icon: CustomersIcon,
        canAccessRoute: ({ classification }) =>
          classification !== 'channel_partner',
        isVisibleInSidebar: ({ classification }) =>
          classification !== 'channel_partner',
        component: Customers,
      },
      {
        id: 'sidebar.partners',
        path: ['/partners', '/partners/:tab'],
        icon: CloudConnection,
        component: Partners,
        canAccessRoute: ({ featureFlags, isAdmin }) =>
          isAdmin && featureFlags?.partners !== false,
        badgeComponent: PartnerToPartnerStatus,
      },
      {
        id: 'sidebar.creditProviders',
        path: ['/credit-providers'],
        icon: EmptyWallet,
        component: CreditProviders,
        canAccessRoute: ({ featureFlags, isAdmin }) =>
          isAdmin && featureFlags?.credit_providers === true,
      },
    ],
  },
  {
    id: 'integrations',
    headerId: '',
    canAccessRoute: ({ featureFlags }) =>
      featureFlags?.integrations_demo === true,
    childRoutes: [
      {
        path: ['/integrations', '/integrations/:integration'],
        id: 'sidebar.integrations',
        icon: Hierarchy3,
        component: IntegrationsDemoPage,
      },
    ],
  },
  {
    id: 'devCenter',
    headerId: '',
    canAccessRoute: ({ pricingPlanFeatures, canView, hasSupportSeat }) => {
      if (pricingPlanFeatures?.device_models_limit === 0) return false;

      return hasSupportSeat || canView('models');
    },
    childRoutes: [
      {
        path: ['/dev-center'],
        hasBorder: true,
        id: 'sidebar.devCenter',
        icon: Code,
        component: DevCenter,
      },
    ],
  },
  {
    id: 'settings',
    headerId: '',
    canAccessRoute: ({ isAdmin }) => isAdmin,
    childRoutes: [
      {
        path: [
          '/settings',
          `/settings/:${SETTING_PAGE_ID_URL_PARAM_NAME}`,
          `/settings/:${SETTING_PAGE_ID_URL_PARAM_NAME}/:tab`,
        ],
        id: 'sidebar.settings',
        icon: Setting2,
        component: Settings,
        children: [
          {
            path: '/settings/store/create-tax-group',
            component: CreateTaxGroup,
          },
        ],
      },
    ],
  },
];

const routeModals: RoutesMap<
  PartnerFeatureFlagsType,
  PartnerPortalCapabilities,
  PartnerPricingPlanFeatures
>['routeModals'] = [
  {
    id: 'customer',
    path: '/m/customers/:customer_id',
    component: Customer,
    backgroundPath: '/customers',
    generatePath: ([customerId]: [string]) => `/m/customers/${customerId}`,
  },
  {
    id: 'order',
    path: '/m/order/:order_id',
    component: Order,
    backgroundPath: '/store-management/orders',
    generatePath: ([orderId]: [string]) => `/m/order/${orderId}`,
  },
  {
    id: 'credit-providers',
    path: '/m/credit-providers',
    component: CreditProvidersRouteModal,
    backgroundPath: '/',
    generatePath: () => `/m/credit-providers`,
  },
  {
    id: 'integrations',
    path: ['/m/integrations', '/m/integrations/:integration'],
    component: IntegrationsRouteModal,
    backgroundPath: '/integrations',
    canAccessRoute: ({ featureFlags }) =>
      Boolean(featureFlags?.integrations_demo),
    generatePath: () => `/m/integrations`,
  },
];

const onboarding = [
  {
    id: 'onboarding',
    path: '/onboarding',
    component: OnboardingWizard,
  },
];

export const routes: RoutesMap<
  PartnerFeatureFlagsType,
  PartnerPortalCapabilities,
  PartnerPricingPlanFeatures
> = {
  dashboard,
  routeModals,
  onboarding,
};
